import React from "react";
import { ArrowRight } from "lucide-react";
import { SOLUTIONS } from "@/data/solutions";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
} from "@/components/ui/carousel";
import { Button } from "@/components/ui/button";

function SolutionSlide({ s, index }) {
  const Icon = s.icon;
  return (
    <div className="rounded-3xl border border-white/10 bg-gradient-to-b from-white/[0.05] to-white/[0.02] p-7 sm:p-10 md:p-12 h-full">
      {/* header */}
      <div className="flex items-center justify-between">
        <span className="rounded-full border border-efory-blue/30 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-efory-blue">
          Solução {s.number}
        </span>
        <span className="rounded-full border border-white/20 px-3 py-1.5 text-xs font-semibold text-white/50">
          {String(index + 1).padStart(2, "0")}/{String(SOLUTIONS.length).padStart(2, "0")}
        </span>
      </div>

      <h3 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight mt-7 text-white leading-[1.05]">
        {s.title}
      </h3>
      <p className="mt-4 text-base sm:text-lg text-white/65 max-w-2xl leading-relaxed">
        {s.subtitle}
      </p>

      <div className="mt-9 grid md:grid-cols-2 gap-6 items-center">
        {/* big icon */}
        <div className="hidden md:flex items-center justify-center">
          <div className="relative h-52 w-52 rounded-full bg-efory-blue/10 flex items-center justify-center">
            <div className="absolute inset-4 rounded-full bg-efory-blue/10" />
            <Icon className="h-24 w-24 text-efory-blue relative" strokeWidth={1.6} />
          </div>
        </div>

        {/* feature list */}
        <div className="space-y-3">
          {s.features.map((f) => {
            const FIcon = f.icon;
            return (
              <div
                key={f.label}
                className="flex items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.03] px-5 py-4 transition-all duration-300 hover:border-efory-blue/40 hover:bg-white/[0.05]"
              >
                <div className="h-11 w-11 shrink-0 rounded-xl bg-efory-blue/10 flex items-center justify-center text-efory-blue">
                  <FIcon className="h-5 w-5" />
                </div>
                <span className="font-heading text-lg font-bold text-white leading-tight">
                  {f.label}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* result banner */}
      <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between rounded-2xl bg-efory-blue/[0.06] border border-efory-blue/20 px-6 py-5">
        <div className="flex items-center gap-4">
          <div className="h-10 w-1 rounded-full bg-efory-blue" />
          <p className="font-heading text-lg sm:text-xl font-bold text-efory-blue">
            {s.result}
          </p>
        </div>
        <a href="#cadastro">
          <Button
            data-testid={`carousel-cta-${s.id}`}
            className="bg-efory-blue hover:bg-efory-bluedark text-white rounded-full px-6 font-semibold transition-all duration-300 hover:scale-105 whitespace-nowrap"
          >
            Quero essa solução
          </Button>
        </a>
      </div>
    </div>
  );
}

export default function SolutionsCarousel() {
  return (
    <section
      id="solucoes-detalhes"
      className="scroll-mt-nav bg-efory-ink text-white py-20 md:py-28 border-t border-white/10"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        <div className="flex flex-col items-center text-center mb-10">
          <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight">
            Cada solução, <span className="text-efory-blue">explicada a fundo</span>
          </h2>
          <p className="mt-4 text-white/65 max-w-xl">
            Arraste para conhecer o que entregamos em cada uma das nossas soluções.
          </p>
        </div>

        <Carousel opts={{ align: "start", loop: true }} className="w-full">
          <CarouselContent>
            {SOLUTIONS.map((s, i) => (
              <CarouselItem key={s.id}>
                <SolutionSlide s={s} index={i} />
              </CarouselItem>
            ))}
          </CarouselContent>
          <div className="flex items-center justify-center gap-4 mt-8">
            <CarouselPrevious
              data-testid="carousel-prev"
              className="static translate-y-0 h-11 w-11 bg-white/5 border-white/20 text-white hover:bg-efory-blue hover:text-white hover:border-efory-blue"
            />
            <span className="text-sm font-medium text-white/50">Arraste para conhecer</span>
            <CarouselNext
              data-testid="carousel-next"
              className="static translate-y-0 h-11 w-11 bg-white/5 border-white/20 text-white hover:bg-efory-blue hover:text-white hover:border-efory-blue"
            />
          </div>
        </Carousel>
      </div>
    </section>
  );
}
